// components/CardView.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
  Divider,
  Box,
  CardMedia
} from '@mui/material';
import { Link } from 'react-router-dom';
import AmazonShopButton from './AmazonShopButton';
import '../style/CardView.css';
import { isProductRecentlyAdded } from '../utils/productDateUtils';

/**
 * Renders a grid of product cards for mobile view
 * 
 * @param {Object} props - Component props
 * @param {Array} props.cards - Array of product data to display as cards
 * @param {boolean} props.isPreview - Render a cleaner compact preview card
 */
const CardView = ({ cards, isPreview = false }) => {
  if (!cards || cards.length === 0) {
    return (
      <Card sx={{ width: '100%', mb: 2 }}>
        <CardContent>
          <Typography variant="body1" align="center">
            No products found matching your criteria.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Helper function to format numeric values
  const getNutrientSource = (product, fieldKey) => product?.nutrientSources?.[fieldKey] || null;
  const getNutrientColor = (product, fieldKey, value) => (
    value !== null && value !== undefined && getNutrientSource(product, fieldKey) === 'guaranteed_analysis'
      ? 'blue'
      : 'inherit'
  );

  const formatValue = (value, source) => {
    if (value === null || value === undefined) return 'N/A';
    const suffix = source === 'guaranteed_analysis' ? '*' : '';
    return `${(value * 1).toFixed(2)}%${suffix}`;
  };

  // Format calories as integer
  const formatCalories = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return Math.round(value) + ' kcal/kg';
  };

  // Parse health benefits into array
  const parseHealthBenefits = (healthBenefits) => {
    if (!healthBenefits) return [];
    if (Array.isArray(healthBenefits)) return healthBenefits.filter(benefit => benefit !== '');
    return [];
  };

  const normalizeRecommendationHighlights = (product) => {
    const highlights =
      product.recommendationHighlights ||
      product.recommendationReasons ||
      product.highlights ||
      [];
    return Array.isArray(highlights) ? highlights.filter(Boolean) : [];
  };

  const hasNutrientValues = (product) => [
    product.proteinDryMatter,
    product.carbsDryMatter,
    product.phosphorusDryMatter,
    product.sodiumDryMatter,
    product.calciumDryMatter,
    product.fatDryMatter
  ].some((value) => value !== null && value !== undefined);

  return (
    <Grid container spacing={isPreview ? 0 : 1} sx={{ width: '100%' }}>
      {cards.map((product, index) => {
        const recommendationHighlights = normalizeRecommendationHighlights(product);
        const productLink = product.id ? `/Product/${product.id}` : null;
        const isRecentlyAdded = isProductRecentlyAdded(product.createDate);

        return (
        <Grid item xs={12} key={product.id || `${product.name}-${index}`} sx={{ width: '100%' }}>
          <Card
            sx={{
              width: '100%',
              mb: isPreview ? 0 : 0.5,
              position: 'relative',
              minWidth: 0,
              overflow: 'hidden',
              boxSizing: 'border-box',
              border: isRecentlyAdded
                ? '2px solid #4fc3f7'
                : (isPreview ? '1px solid #d8e2ef' : '1px solid #999'),
              borderRadius: isPreview ? 2 : 1,
              boxShadow: isPreview ? '0 10px 24px rgba(0,0,0,0.14)' : 'none'
            }}
          >
            {isRecentlyAdded && (
              <Chip
                label="Recently added"
                size="small"
                color="info"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 1,
                  fontWeight: 700
                }}
              />
            )}

            {/* Rank Badge - Only shown if rank exists */}
            {product.rank && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 2,
                  py: 0.8,
                  borderBottomLeftRadius: 10,
                  zIndex: 1,
                  fontSize: '1.1rem'
                }}
              >
                #{product.rank}
              </Box>
            )}

            <Grid
              container
              sx={{ minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              direction={{ xs: 'column', sm: 'row' }}
            >
              <Grid
                item
                xs={12}
                sm={3}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 0,
                  boxSizing: 'border-box'
                }}
              >
                <Box sx={{ pr: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'center' }}>
                  {product.cloudinaryUrl && productLink && (
                    <Link to={productLink} style={{ textDecoration: 'none' }}>
                      <CardMedia
                        component="img"
                        image={product.cloudinaryUrl}
                        alt={product.name}
                        sx={{
                          height: isPreview ? 110 : 140,
                          objectFit: 'contain',
                          p: 1
                        }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </Link>
                  )}
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sm={9}
                sx={{
                  minWidth: 0,
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <CardContent
                  sx={{
                    p: isPreview ? 1.5 : 2,
                    '&:last-child': { pb: isPreview ? 1.5 : 2 },
                    boxSizing: 'border-box'
                  }}
                >
                  {/* Product Name as Link */}
                  <Typography
                    variant={isPreview ? "subtitle1" : "h6"}
                    component="div"
                    gutterBottom
                    sx={{
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere'
                    }}
                  >
                    {productLink ? (
                      <Link
                        to={productLink}
                        className="link-style"
                        style={{
                          textDecoration: 'none',
                          color: '#1976d2',
                          lineHeight: isPreview ? 1.25 : 'normal'
                        }}
                      >
                        {product.name}
                      </Link>
                    ) : (
                      product.name
                    )}
                  </Typography>

                  {(product.brand || product.line) && (
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      {product.brand} {product.line ? `- ${product.line}` : ''}
                    </Typography>
                  )}

                  {product.category && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Category:</strong> {product.category}
                    </Typography>
                  )}

                  {product.texture && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Texture:</strong> {product.texture}
                    </Typography>
                  )}

                  {product.format && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Format:</strong> {Array.isArray(product.format) ? product.format.join(', ') : product.format}
                    </Typography>
                  )}

                  {product.lifestage && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Lifestage:</strong> {Array.isArray(product.lifestage) ? product.lifestage.join(', ') : product.lifestage}
                    </Typography>
                  )}

                  {product.proteins && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Proteins:</strong> {Array.isArray(product.proteins) ? product.proteins.join(', ') : product.proteins}
                    </Typography>
                  )}

                  {recommendationHighlights.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Why The Picky Kitty recommends it:</strong>
                      </Typography>
                      <ul className="recommendation-reasons">
                        {recommendationHighlights.map((reason, index) => (
                          <li key={`${product.id || product.name}-reason-${index}`}>{reason}</li>
                        ))}
                      </ul>
                    </Box>
                  )}

                  {product.caloriesPerKg && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Calories:</strong> {formatCalories(product.caloriesPerKg)}
                    </Typography>
                  )}

                  {/* Tag Chips - Now below product details but in the right column */}
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0, mb: 0 }} // Reduced margin around the chips
                  >
                    {/* Only display chips if there are any */}

                    {product.isLid && (
                      <Chip
                        label="Limited Ingredient Diet"
                        size="small"
                        variant="outlined"
                        color="success" 
                        sx={{ mb: 0.5 }}
                      />
                    )}

                    {product.isGrainFree && (
                      <Chip
                        label="Grain-Free"
                        size="small"
                        variant="outlined"
                        color="success"
                        sx={{ mb: 0.5 }}  // Reduced bottom margin for individual chips
                      />
                    )}

                    {product.isGmoFree && (
                      <Chip
                        label="GMO-Free"
                        size="small"
                        variant="outlined"
                        color="success"
                        sx={{ mb: 0.5 }}
                      />
                    )}

                    {product.containsRawFood && (
                      <Chip
                        label="Contains Raw"
                        size="small"
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                    )}

                    {product.isPrescription && (
                      <Chip
                        label="Prescription"
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ mb: 0.5 }}
                      />
                    )}

                    {/* Health Benefits Chips */}
                    {product.healthBenefits &&
                      parseHealthBenefits(product.healthBenefits).map((benefit, index) => (
                        <Chip
                          key={index}
                          label={benefit}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ mb: 0.5 }} // Reduced bottom margin for individual chips
                        />
                      ))
                    }
                  </Stack>
                </CardContent>
              </Grid>
            </Grid>

            <CardContent sx={{ pt: 0, boxSizing: 'border-box' }}>
              {hasNutrientValues(product) && (
                <>
                  {/* Nutritional Values Section */}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Nutritional Values (Dry Matter Basis):
                  </Typography>

                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ display: 'flex', gap: 1 }}>
                        <strong>Protein:</strong>
                        <span style={{
                          color: getNutrientColor(product, 'proteinDryMatter', product.proteinDryMatter)
                        }}>
                          {formatValue(product.proteinDryMatter, getNutrientSource(product, 'proteinDryMatter'))}
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ display: 'flex', gap: 1 }}>
                        <strong>Carbs:</strong>
                        <span style={{
                          color: getNutrientColor(product, 'carbsDryMatter', product.carbsDryMatter)
                        }}>
                          {formatValue(product.carbsDryMatter, getNutrientSource(product, 'carbsDryMatter'))}
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ display: 'flex', gap: 1 }}>
                        <strong>Phosphorus:</strong>
                        <span style={{
                          color: getNutrientColor(product, 'phosphorusDryMatter', product.phosphorusDryMatter)
                        }}>
                          {formatValue(product.phosphorusDryMatter, getNutrientSource(product, 'phosphorusDryMatter'))}
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ display: 'flex', gap: 1 }}>
                        <strong>Sodium:</strong>
                        <span style={{
                          color: getNutrientColor(product, 'sodiumDryMatter', product.sodiumDryMatter)
                        }}>
                          {formatValue(product.sodiumDryMatter, getNutrientSource(product, 'sodiumDryMatter'))}
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ display: 'flex', gap: 1 }}>
                        <strong>Calcium:</strong>
                        <span style={{
                          color: getNutrientColor(product, 'calciumDryMatter', product.calciumDryMatter)
                        }}>
                          {formatValue(product.calciumDryMatter, getNutrientSource(product, 'calciumDryMatter'))}
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ display: 'flex', gap: 1 }}>
                        <strong>Fat:</strong>
                        <span style={{
                          color: getNutrientColor(product, 'fatDryMatter', product.fatDryMatter)
                        }}>
                          {formatValue(product.fatDryMatter, getNutrientSource(product, 'fatDryMatter'))}
                        </span>
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}
              
              {/* Amazon Shop Button */}
              {!isPreview && product.amazonUrl && (
                <Box sx={{ 
                  mt: 2, 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  boxSizing: 'border-box'
                }}>
                  <AmazonShopButton 
                    amazonUrl={product.amazonUrl} 
                    size="small" 
                    fullWidth={true}
                  />
                </Box>
              )}
            </CardContent>

            {/* Last Update Date - At the very bottom of the card */}
            {!isPreview && product.lastUpdateDate && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block', 
                  textAlign: 'right', 
                  mt: 0.5, 
                  mb: 1,
                  fontSize: '0.75rem',
                  pr: 2
                }}
              >
                Last updated: {new Date(product.lastUpdateDate).toLocaleDateString()}
              </Typography>
            )}
          </Card>
        </Grid>
      )})}
    </Grid>
  );
};

export default CardView;